import {produce} from 'immer'
import type {StateCreator} from 'zustand'

export type PendingCommentThreads = {
  threadId: string
}

export type PendingReview = {
  // id is undefined when there is no pending review for the viewer
  id?: string
  // we don't actually care about the comments, this type is to be consistent with the data returned from the backend
  // and will be modified in a follow up PR so that this PR is not too bloated
  comments: PendingCommentThreads[]
}

export type PendingReviewIDs = PendingReview & {
  /**
   * pendingReviewIDs represents the IDs of threads that have pending comments.
   */
  pendingReviewIDs?: number[]
}

export interface PendingReviewSliceOpts {
  pendingReview: PendingReviewIDs
}

export type PendingReviewSlice = {
  pendingReview: PendingReviewIDs

  pendingReviewActions: {
    addPendingComment: (commentId: number) => void
    removePendingComment: (commentId: number) => void
    clearPendingReview: () => void
  }
}

export const createPendingReviewSlice =
  ({
    pendingReview,
  }: PendingReviewSliceOpts): StateCreator<PendingReviewSlice, [['zustand/devtools', never]], [], PendingReviewSlice> =>
  (set, get) => ({
    pendingReview,

    pendingReviewActions: {
      addPendingComment: commentId => {
        const oldPendingReview = get().pendingReview
        if (!oldPendingReview) {
          return
        }

        set(
          {
            pendingReview: produce(oldPendingReview, (m: PendingReviewIDs) => {
              if (!m.pendingReviewIDs) {
                m.pendingReviewIDs = []
              }
              m.pendingReviewIDs.push(commentId)
            }),
          },
          undefined,
          'addPendingComment',
        )
      },
      removePendingComment: commentId => {
        const oldPendingReview = get().pendingReview
        if (!oldPendingReview) {
          return
        }

        set(
          {
            pendingReview: produce(oldPendingReview, m => {
              const pendingReviewsAfterChange = m.pendingReviewIDs?.filter(id => id !== commentId)
              m.pendingReviewIDs = pendingReviewsAfterChange
            }),
          },
          undefined,
          'removePendingComment',
        )
      },
      clearPendingReview: () => {
        const oldPendingReview = get().pendingReview
        if (!oldPendingReview) {
          return
        }

        set(
          {
            pendingReview: produce(oldPendingReview, (m: PendingReviewIDs) => {
              m.id = undefined
              m.pendingReviewIDs = []
              m.comments = []
            }),
          },
          undefined,
          'clearPendingReview',
        )
      },
    },
  })
